import {
  FileReference,
  SiteObject,
  TemplateElementGroup,
} from "./baseEntities";

export interface OnlineTemplate extends BaseOnlineTemplate, SiteObject {}

export interface BaseOnlineTemplate {
  code: string;
  name: string;
  active: boolean;
  default: boolean;

  /**
   * Global
   */
  global: {
    font: FontFamily;
    button: {
      primaryButtonColour: string;
      primaryButtonBorderColour: string;
      primaryButtonFontColour: string;
      hoverPrimaryButtonColour: string;
      hoverPrimaryButtonBorderColour: string;
      hoverPrimaryButtonFontColour: string;
      secondButtonColour: string;
      secondButtonBorderColour: string;
      secondButtonFontColour: string;
      hoverSecondButtonColour: string;
      hoverSecondButtonBorderColour: string;
      hoverSecondButtonFontColour: string;
      buttonSize: Size;
      buttonStyle: Shape;
    };
    input: {
      backgroundColour: string;
      borderColour: string;
      focusBackgroundColour: string;
      focusBorderColour: string;
      fontColour: string;
    };
    icon: {
      colour: string;
    };
    links: {
      termsAndConditions: string;
      privacyPolicy: string;
    };
  };

  /**
   * NavBar
   */
  navBar: {
    backgroundColour: string;
    borderColour: string;
    logo: FileReference | null;
    size: Size;
  };

  /**
   * Menu Bar. SideBar or TopBar
   */
  menuBar: {
    site: {
      showSiteIcon: boolean;
    };
    general: {
      position: MenuPosition;
      backgroundColour: string;
      secondaryBackgroundColour: string;
      showSocials: boolean;
      showCopyright: boolean;
    };
    menuItem: {
      fontSize: Size;
      fontColour: string;
      showIcons: boolean;
      iconColour: string;
      items: NavBarMenuItem[];
    };
    topBar: {
      image: FileReference | null;
      title: string;
      titleFontSize: Size;
      titleFontColour: string;
      description: string;
      descriptionFontSize: Size;
      descriptionFontColour: string;
    };
  };

  footer: FooterType;

  branding: {
    copyrightMessage: string | null;
    socials: SocialItem[];
  };

  /**
   * InfoBar
   */
  infoBar: {
    general: {
      backgroundColour: string;
      fontColour: string;
    };
    orderTime: {
      changeButtonColour: string;
    };
    authButton: {
      colour: string;
      fontColour: string;
      hoverColour: string;
      hoverFontColour: string;
      style: Shape;
    };
    points: {
      fontSize: Size;
      fontColour: string;
    };
    mobilePoints: {
      fontColour: string;
      viewButtonColour: string;
    };
  };

  /**
   * Select store screen
   */
  storesScreen: {
    general: {
      backgroundColour: string;
      titleFontSize: Size;
      titleFontColour: string;
    };
    card: {
      backgroundColour: string;
      fontSize: Size;
      fontColour: string;
      stroke: boolean;
      strokeColour: string;
      buttonColour: string;
      buttonFontColour: string;
      buttonStyle: Shape;
    };
  };

  /**
   * Banners
   */
  banners: BannerItem[];

  /**
   * Menu screen
   */
  menuScreen: {
    general: {
      backgroundColour: string;
    };
    tabs: {
      widthImage: boolean;
      imageShape: Shape;
      position: TabsPosition;
      backgroundColour: string;
      borderColour: string;
      shadow: boolean;
      itemBackgroundColour: string;
      itemBorderColour: string;
      itemFontColour: string;
      selectedItemBackgroundColour: string;
      selectedItemBorderColour: string;
      selectedItemFontColour: string;
      fontSize: Size;
      itemType: Shape;
    };
    category: {
      fontColour: string;
      fontSize: Size;
      fontFamily: FontFamily;
      fontWeight: FontWeight;
      fontTransform: TextTransform;
    };
    card: {
      backgroundColour: string;
      borderColour: string;
      shadow: boolean;
      titleFontSize: Size;
      titleColour: string;
      showDescription: boolean;
      descriptionFontSize: Size;
      descriptionColour: string;
      priceFontSize: Size;
      priceColour: string;
      imageBackgroundColour: string;
      imageSize: Size;
      imageShape: Shape;
    };
  };

  /**
   * Product screen
   */
  productScreen: {
    general: {
      backgroundColour: string;
      sectionBackgroundColour: string;
      sectionStroke: boolean;
      sectionStrokeColour: string;
    };
    info: {
      imageBackgroundColour: string;
      titleFontSize: Size;
      titleColour: string;
      showDescription: boolean;
      descriptionFontSize: Size;
      descriptionColour: string;
      descriptionExpandButtonColour: string;
      priceFontSize: Size;
      priceColour: string;
      showAllergens: boolean;
      showAllergensIcon: boolean;
      allergensFontSize: Size;
      allergensFontColour: string;
      allergensLink: string;
      allergensLinkColour: string;
    };
    modifiers: {
      headerBackgroundColour: string;
      headerFontSize: Size;
      headerFontColour: string;
      fontSize: Size;
      fontColour: string;
      priceColour: string;
    };
  };

  /**
   * Cart
   */
  cart: {
    position: CartPosition;
    backgroundColour: string;
    fontColour: string;
    showAdditionalProducts: boolean;
    item: {
      imageBackgroundColour: string;
      nameFontSize: Size;
      nameFontColour: string;
      modifiersFontSize: Size;
      modifiersFontColour: string;
      priceFontSize: Size;
      priceFontColour: string;
    };
    offer: {
      icon: FileReference | null;
      backgroundColour: string;
      fontColour: string;
    };
  };

  /**
   * Loyalty Dialog
   */
  loyaltyDialog: {
    general: {
      titleFontSize: Size;
      titleFontColour: string;
      subtitleFontSize: Size;
      subtitleFontColour: string;
      pointsFontColour: string;
    };
    targetPoints: {
      show: boolean;
      backgroundColour: string;
      progressBarColour: string;
      points: number;
    };
    card: {
      backgroundColour: string;
      borderColour: string;
      titleFontSize: Size;
      titleFontColour: string;
      descriptionFontSize: Size;
      descriptionFontColour: string;
      buttonColour: string;
      buttonBorderColour: string;
      buttonFontColour: string;
      buttonSize: Size;
      buttonStyle: Shape;
      buttonHoverColour: string;
      buttonHoverBorderColour: string;
      buttonHoverFontColour: string;
    };
  };

  /**
   * Loyalty
   */
  loyalty: {
    text: {
      viewYourClubGoods: string;
      joinOurLoyaltyProgram: string;
      haveAVoucherCode: string;
      usePromotion: string;
      loginOrSignUp: string;
    };
  };

  /**
   * Checkout screen
   */
  checkoutScreen: {
    general: {
      backgroundColour: string;
      sectionBackgroundColour: string;
      sectionStroke: boolean;
      sectionStrokeColour: string;
    };
    paymentMethod: {
      backgroundColour: string;
      borderColour: string;
      fontColour: string;
      selectedBackgroundColour: string;
      selectedBorderColour: string;
      selectedFontColour: string;
    };
  };
}

export type SocialItem = {
  url: string;
  icon: SocialIcon;
  customIcon: FileReference | null;
};

export type BannerItem = {
  general: {
    backgroundColour: string;
    backgroundImage: FileReference | null;
    showInDesktop: boolean;
    showInMobile: boolean;
  };
  dots: {
    colour: string;
    activeColour: string;
  };
  title: {
    show: boolean;
    text: string;
    fontSize: Size;
    fontColour: string;
  };
  subtitle: {
    show: boolean;
    text: string;
    fontSize: Size;
    fontColour: string;
  };
  description: {
    show: boolean;
    text: string;
    fontSize: Size;
    fontColour: string;
  };
  button: {
    show: boolean;
    text: string;
    backgroundColour: string;
    fontColour: string;
    hoverBackgroundColour: string;
    hoverFontColour: string;
    size: Size;
    style: Shape;
  };
  productCode: string | null;
};

export enum Size {
  S = "S",
  M = "M",
  L = "L",
}

export enum Shape {
  Circle = "CIRCLE",
  Box = "BOX",
  RoundedBox = "ROUNDED_BOX",
  FullyRoundedBox = "FULLY_ROUNDED_BOX",
}

export enum CartPosition {
  AtTheTopRightCorner = "AT_THE_TOP_RIGHT_CORNER",
  OnTheSide = "ON_THE_SIDE",
}

export enum TabsPosition {
  Top = "TOP",
  Bottom = "BOTTOM",
}

export enum SocialIcon {
  Instagram = "instagram",
  Facebook = "facebook",
  Twitter = "twitter",
  Pinterest = "pinterest",
  Tiktok = "tiktok",
  Youtube = "youtube",
  Yelp = "yelp",
  Google = "google",
  Discord = "discord",
  Linkedin = "linkedin",
  Intercom = "intercom",
  Apple = "apple",
  Android = "android",
  Generic = "generic",
}

export interface FooterType {
  showFooter: boolean;
  primaryFontColour: string;
  secondaryFontColour: string;
  backgroundColour: string;
  groups: TemplateElementGroup[];
}

export enum FontFamily {
  Montserrat = "Montserrat",
  InstrumentSans = "Instrument Sans",
  VanguardCF = "Vanguard CF",
}

export enum FontWeight {
  Normal = "normal",
  Bold = "bold",
}

export enum TextTransform {
  None = "none",
  Uppercase = "uppercase",
}

export enum MenuPosition {
  Side = "SIDE",
  Top = "TOP",
}

export type NavBarMenuItem = {
  title: string;
  description: string;
  link: string;
  icon: FileReference | null;
  column: number;
};
